import { useQuery } from "react-query";

import { getSwapPath } from "../../../../api/swap";
import { useChains } from "../../../../components/FetchConfig";
import { useAlerting, useTranslation } from "../../../../hooks";
import { useAppSelector } from "../../../../reducer/hooks";
import { selectProfile } from "../../../../reducer/profileSlice";
import { findDecimalByChainNameAndAsset } from "../../../../utils/helper";
import Button from "../components/Button";
import SwapActions from "../components/SwapActions";
import { useCreateSwapRequestContext } from "../context";
import { initExactGetFields } from "../context/CreateSwapRequestContext";
import { useFormFields } from "../hooks";
import { useExactGetQuote } from "../hooks/useExactGetQuote";
import { useExactGetSwap } from "../hooks/useExactGetSwap";
import { TabTypes } from "../types";
import AmountTextField from "./components/AmountTextField";
import Balance from "./components/Balance";
import QuoteDetails from "./components/QuoteDetails";
import SmartAccountType from "./components/SmartAccountType";
import WalletAddressField from "./components/WalletAddressField";
import ContentContainer from "./layout/ContentContainer";
import ContentRow from "./layout/ContentRow";
import HeaderRow from "./layout/HeaderRow";
import InnerContent from "./layout/InnerContent";

const ExactGetContent = () => {
  const {
    filterFields,
    exactGetFields,
    setExactGetFields,
    resetEverything,
    swapPaths,
    setSwapPaths,
  } = useCreateSwapRequestContext();

  const { t } = useTranslation("createSwapRequest");
  const { alerting } = useAlerting();

  const { selectChainByPieces } = useChains();
  const { merchantId } = useAppSelector(selectProfile);
  const { chain_type, chain_id } = selectChainByPieces({
    chain_name: filterFields.chainName,
  });

  const { updateWalletAddress, updateAmount } = useFormFields({
    fields: exactGetFields,
    setFields: setExactGetFields,
    initFields: initExactGetFields,
    quoteFields: ["estimateAmount", "exchangeRate", "payMax", "quotedTime"],
  });

  const fromCurrencyDecimals = Number(
    findDecimalByChainNameAndAsset(
      String(chain_type),
      String(chain_id),
      filterFields.fromCurrency
    )
  );
  const toCurrencyDecimals = Number(
    findDecimalByChainNameAndAsset(
      String(chain_type),
      String(chain_id),
      filterFields.toCurrency
    )
  );

  const { handleQuote } = useExactGetQuote({ swapPaths });

  const { handleSwap } = useExactGetSwap({
    swapPaths,
  });

  const handleReset = () => {
    resetEverything(true);
  };

  useQuery({
    queryKey: ["swap-path", chain_type, chain_id],
    queryFn: () => {
      return getSwapPath({
        chainType: Number(chain_type),
        chainId: Number(chain_id),
      });
    },
    enabled: Boolean(chain_type && chain_id),
    onSuccess: (data) => {
      setSwapPaths(data.rows);
    },
  });

  return (
    <ContentContainer>
      <HeaderRow>{t("exactGet")}</HeaderRow>
      <InnerContent>
        <div className="split">
          <div className="form">
            <ContentRow title={t("walletAddress")}>
              <WalletAddressField
                addressBookOrder={exactGetFields.addressBookOrder}
                chainName={filterFields.chainName}
                onFieldChange={updateWalletAddress}
              />
            </ContentRow>

            <ContentRow title={t("smartAccountType")}>
              <SmartAccountType fields={exactGetFields} />
            </ContentRow>

            <ContentRow title={t("balance")}>
              <Balance
                chainType={Number(chain_type)}
                chainId={Number(chain_id)}
                assetName={filterFields.fromCurrency}
                decimals={String(fromCurrencyDecimals)}
                walletAddress={exactGetFields.rawWalletInfo?.wallet_address}
                balance={exactGetFields.balance}
                updateBalance={(balance: string) => {
                  setExactGetFields({ ...exactGetFields, balance });
                }}
              />
            </ContentRow>

            <ContentRow title={t("exactGetAmount")}>
              <AmountTextField
                label={t("peExactGetAmount")}
                value={exactGetFields.exactGetAmount}
                onChange={(value) => updateAmount(value, "exactGetAmount")}
                currency={filterFields.toCurrency}
                decimalLimit={toCurrencyDecimals}
              />
            </ContentRow>

            <div className="quote-btn">
              <Button onClick={handleQuote}>{t("quote")}</Button>
            </div>
          </div>

          <QuoteDetails
            fields={exactGetFields}
            t={t}
            estimateField="estimateAmount"
            minMaxField="payMax"
            estimateLabel="estimatePayAmount"
            minMaxLabel="payMax"
            mode={TabTypes.EXACT_GET}
            amountCurrency={filterFields.fromCurrency}
            amountFormulaString={t("maxPayAmountFormula", {
              maxSlippage: filterFields.maxSlippage,
            })}
          />
        </div>
      </InnerContent>

      <SwapActions onSwap={handleSwap} onReset={handleReset} />
    </ContentContainer>
  );
};

export default ExactGetContent;
